if (!customElements.get('ring-builder-media')) {
  customElements.define('ring-builder-media', class extends HTMLElement {
    connectedCallback() {
      this.config = JSON.parse(this.querySelector('[data-gallery-config]').textContent);
      this.productInfo = this.closest('product-info');
      this.stage = this.querySelector('.rb-media__stage');
      this.frame = this.querySelector('iframe');
      this.message = this.querySelector('.rb-media__message');
      this.buttons = [...this.querySelectorAll('[data-show]')];
      this.allImages = this.buttons.filter(button => button.dataset.show !== 'viewer');
      this.images = this.allImages.filter(button => !button.hidden);
      this.active = this.images.find(button => button.getAttribute('aria-pressed') === 'true')?.dataset.show || 'viewer';
      this.variant = this.config.variant;
      this.managedViewer = !!this.dataset.renewalEndpoint;
      this.renewalViews = new Map();
      try { this.metalPreviews = this.dataset.metalPreviews ? JSON.parse(this.dataset.metalPreviews) : null; }
      catch { this.previewConfigInvalid = true; }
      const previewNote = this.querySelector('.rb-media__preview-note');
      if ((this.managedViewer || this.metalPreviews) && previewNote) previewNote.textContent = this.config.labels.liveNote;
      this.listeners = new AbortController();
      const signal = this.listeners.signal;
      this.addEventListener('click', event => {
        const show = event.target.closest('[data-show]');
        const step = event.target.closest('[data-step]');
        if (show) this.select(show.dataset.show);
        if (step) this.step(Number(step.dataset.step));
        if (event.target.closest('[data-retry]')) this.refreshViewer();
      }, { signal });
      this.stage.addEventListener('keydown', event => {
        if (event.target !== this.stage || this.active === 'viewer') return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.step(event.key === 'ArrowRight' ? 1 : -1);
        }
      }, { signal });
      this.stage.addEventListener('pointerdown', event => {
        if (event.pointerType === 'touch' && this.active !== 'viewer') this.touch = [event.clientX, event.clientY];
      }, { signal });
      this.stage.addEventListener('pointerup', event => {
        if (!this.touch) return;
        const dx = event.clientX - this.touch[0];
        const dy = event.clientY - this.touch[1];
        this.touch = null;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) this.step(dx < 0 ? 1 : -1);
      }, { signal });
      this.stage.addEventListener('pointercancel', () => { this.touch = null; }, { signal });
      this.productInfo?.addEventListener('change', event => {
        if (!this.managedViewer && event.target.name === 'properties[Ring Size]') this.selectionChanged();
        if (event.target.closest('variant-selects')) {
          this.invalidate();
          this.filterImages(this.variant, this.currentFeaturedMediaId, false);
        }
        if (event.target.matches('input[name="id"]') && !event.target.value) {
          this.variant = null;
          this.selectionChanged();
          this.showMessage('unavailable');
        }
      }, { signal });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, event => {
          if (String(event.data.sectionId) !== this.dataset.sectionId) return;
          this.variant = event.data.variant;
          this.selectionChanged();
          const nextConfigElement = event.data.html?.getElementById(this.id)?.querySelector('[data-gallery-config]');
          const nextConfig = nextConfigElement ? JSON.parse(nextConfigElement.textContent) : null;
          this.filterImages(this.variant, nextConfig?.featuredMediaId ?? this.variant?.featured_media?.id);
        });
      }
      document.addEventListener('ring-builder:selection-change', event => {
        if (String(event.detail?.productId) !== String(this.config.productId)) return;
        this.additionalSelection = event.detail.parameters || {};
        this.selectionChanged();
      }, { signal });
      document.addEventListener('visibilitychange', () => {
        if (this.managedViewer) {
          if (!document.hidden && this.active === 'viewer') this.refreshViewer();
          return;
        }
        if (!document.hidden && this.dataset.previewUrl && Date.now() >= Date.parse(this.dataset.previewExpires)) this.expirePreview();
        if (!document.hidden && this.active === 'viewer' && this.expiresAt && Date.now() >= this.expiresAt - 30000) this.refreshViewer();
      }, { signal });
      this.filterImages(this.variant, this.config.featuredMediaId);
      if (this.managedViewer || this.metalPreviews || this.dataset.previewUrl || !this.images.length) this.select('viewer', false);
    }

    filterImages(variant, featuredMediaId, reset = true) {
      this.currentFeaturedMediaId = featuredMediaId;
      const featured = this.allImages.find(button => button.dataset.show === String(featuredMediaId || ''));
      let visible = featured ? this.allImages.filter(button => button.dataset.mediaGroup === featured.dataset.mediaGroup) : this.allImages;
      if (this.config.groupedMedia) {
        const inputs = this.productInfo?.querySelectorAll('variant-selects input:checked, variant-selects select') || [];
        for (const input of inputs) {
          const source = input.tagName === 'SELECT' ? input.selectedOptions?.[0]?.dataset : input.dataset;
          const grouped = source?.variantImageGrouped?.trim();
          const keys = grouped ? grouped.split(',').map(key => key.trim().toLowerCase().replace(/[^a-z0-9]/g, '-')).filter(Boolean) : [source?.filter?.replace(/^\.?filter-/, '').trim()].filter(Boolean);
          const matches = visible.filter(button => keys.includes(button.dataset.imageFilter));
          if (matches.length) visible = matches;
        }
      }
      this.images = visible;
      this.buttons = [this.querySelector('[data-show="viewer"]'), ...visible];
      this.allImages.forEach(button => { button.hidden = !visible.includes(button); });
      if (this.active !== 'viewer') {
        const next = reset && featured && visible.includes(featured) ? featured : visible.find(button => button.dataset.show === this.active) || visible[0];
        if (next) this.select(next.dataset.show, false);
        else this.select('viewer', false);
      } else {
        this.querySelectorAll('[data-panel]').forEach(panel => { panel.hidden = panel.dataset.panel !== 'viewer'; });
        this.allImages.forEach(button => button.setAttribute('aria-pressed', 'false'));
        this.updateArrows();
      }
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.unsubscribe?.();
      clearTimeout(this.debounce);
      clearTimeout(this.renewal);
      clearTimeout(this.frameTimeout);
      clearTimeout(this.previewExpiryTimer);
      this.request?.abort();
    }

    select(id, announce = true) {
      if (!this.buttons.some(button => button.dataset.show === id)) return;
      this.active = id;
      this.querySelectorAll('[data-panel]').forEach(panel => { panel.hidden = panel.dataset.panel !== id; });
      this.buttons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.show === id)));
      if (id === 'viewer') {
        if (this.selectionDirty || !this.viewerReady || Date.now() >= this.expiresAt - 30000) this.refreshViewer();
      } else {
        const button = this.images.find(item => item.dataset.show === id);
        const scroll = this.querySelector('.rb-media__scroll');
        const left = button.offsetLeft - scroll.offsetLeft;
        if (left < scroll.scrollLeft) scroll.scrollLeft = left;
        if (left + button.offsetWidth > scroll.scrollLeft + scroll.clientWidth) scroll.scrollLeft = left + button.offsetWidth - scroll.clientWidth;
      }
      this.updateArrows();
      if (announce) this.querySelector('[data-gallery-status]').textContent = id === 'viewer' ? this.config.labels.viewer : this.buttons.find(button => button.dataset.show === id).getAttribute('aria-label');
    }

    updateArrows() {
      const arrows = this.querySelector('.rb-media__arrows');
      const index = this.images.findIndex(button => button.dataset.show === this.active);
      arrows.hidden = this.active === 'viewer' || this.images.length < 2;
      this.querySelector('[data-step="-1"]').disabled = index <= 0;
      this.querySelector('[data-step="1"]').disabled = index >= this.images.length - 1;
    }

    step(direction) {
      if (this.active === 'viewer') return;
      const index = this.images.findIndex(button => button.dataset.show === this.active);
      const target = this.images[index + direction];
      if (target) this.select(target.dataset.show);
    }

    showMessage(label, retry = false) {
      this.stage.setAttribute('aria-busy', 'false');
      this.frame.hidden = true;
      this.message.hidden = false;
      this.querySelector('[data-viewer-message]').textContent = this.config.labels[label];
      this.querySelector('[data-retry]').hidden = !retry;
    }

    invalidate() {
      this.generation = (this.generation || 0) + 1;
      this.selectionDirty = true;
      if (!this.viewerReady) this.showMessage(this.managedViewer || this.dataset.endpoint ? 'loading' : 'pending');
    }

    selectionChanged() {
      this.invalidate();
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => { if (this.active === 'viewer') this.refreshViewer(); }, 300);
    }

    selection() {
      const size = this.productInfo?.querySelector('[name="properties[Ring Size]"]')?.value || '';
      return {
        productId: String(this.config.productId),
        productHandle: this.config.productHandle,
        variantId: this.variant?.id ? String(this.variant.id) : null,
        selectedOptions: this.config.optionNames.map((name, index) => ({ name, value: this.variant?.options?.[index] || '' })),
        ringSize: size,
        parameters: this.additionalSelection || {}
      };
    }

    async refreshViewer() {
      if (this.managedViewer) { await this.refreshRenewingPreview(); return; }
      if (this.previewConfigInvalid) { this.showMessage('error', true); return; }
      if (this.metalPreviews) { this.openMetalPreview(); return; }
      if (this.dataset.previewUrl) { this.openDesignPreview(); return; }
      if (!this.dataset.endpoint) { this.showMessage('pending'); return; }
      if (this.busy) { this.queued = true; return; }
      let endpoint;
      try {
        endpoint = new URL(this.dataset.endpoint, location.origin);
        if (endpoint.origin !== location.origin || !endpoint.pathname.startsWith('/apps/')) throw new Error();
      } catch { this.showMessage('error', true); return; }
      if (!this.variant?.id) { this.showMessage('unavailable'); return; }
      this.busy = true;
      const generation = this.generation || 0;
      if (!this.viewerReady) this.showMessage('loading');
      this.stage.setAttribute('aria-busy', 'true');
      this.request = new AbortController();
      const timeout = setTimeout(() => this.request.abort(), 20000);
      try {
        const response = await fetch(endpoint.href, {
          method: 'POST',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId: this.sessionId || null, selection: this.selection() }),
          signal: this.request.signal
        });
        if (!response.ok) throw new Error();
        const session = await response.json();
        if (!this.isConnected) return;
        if (typeof session.sessionId !== 'string' || !session.sessionId) throw new Error();
        this.sessionId = session.sessionId;
        if (generation !== (this.generation || 0)) { this.queued = true; return; }
        const url = new URL(session.viewerUrl);
        const expiresAt = Date.parse(session.viewerExpiresAt);
        if (url.protocol !== 'https:' || url.hostname !== 'app.pencildesign.co' || url.username || url.password || url.port || !Number.isFinite(expiresAt) || expiresAt <= Date.now() + 30000) throw new Error();
        this.selectionDirty = false;
        this.expiresAt = expiresAt;
        if (this.frame.getAttribute('src') === url.href && this.viewerReady) {
          this.frame.hidden = false;
          this.message.hidden = true;
          this.scheduleRenewal();
          return;
        }
        clearTimeout(this.frameTimeout);
        this.frame.onload = null;
        this.frame.onerror = null;
        const previousFrame = this.frame;
        this.frame = previousFrame.cloneNode(false);
        this.frame.removeAttribute('src');
        previousFrame.replaceWith(this.frame);
        this.viewerReady = false;
        const loadingFrame = this.frame;
        this.frame.onload = () => {
          if (this.frame !== loadingFrame || !this.isConnected) return;
          clearTimeout(this.frameTimeout);
          this.viewerReady = true;
          this.frame.hidden = false;
          this.message.hidden = true;
          this.scheduleRenewal();
        };
        this.frame.onerror = () => { clearTimeout(this.frameTimeout); this.showMessage('error', true); };
        this.frame.src = url.href;
        this.frameTimeout = setTimeout(() => { this.frame.onload = null; this.showMessage('error', true); }, 30000);
      } catch {
        if (this.isConnected && generation === (this.generation || 0)) this.showMessage('error', true);
      } finally {
        clearTimeout(timeout);
        this.stage.setAttribute('aria-busy', 'false');
        this.busy = false;
        if (this.queued && this.isConnected) {
          this.queued = false;
          if (this.active === 'viewer') this.refreshViewer();
        }
      }
    }

    scheduleRenewal() {
      clearTimeout(this.renewal);
      this.renewal = setTimeout(() => {
        if (this.active === 'viewer' && !document.hidden) this.refreshViewer();
      }, Math.max(1000, this.expiresAt - Date.now() - 30000));
    }

    async refreshRenewingPreview() {
      if (this.busy) { this.queued = true; return; }
      const metal = this.selection().selectedOptions.find(option => option.name.toLowerCase() === 'metal')?.value?.trim().toLowerCase().replace(/\s+/g, ' ');
      if (!this.variant?.id || !metal) { this.showMessage('unavailable'); return; }
      let endpoint;
      try {
        endpoint = new URL(this.dataset.renewalEndpoint);
        if (endpoint.origin !== 'https://atelierrings-pencil-sessions.coppershark71.chatgpt.site' || endpoint.pathname !== '/api/view' || endpoint.search || endpoint.hash || endpoint.username || endpoint.password) throw new Error();
      } catch { this.showMessage('error', true); return; }
      const cached = this.renewalViews.get(metal);
      if (cached && Date.parse(cached.viewerExpiresAt) > Date.now() + 300000) {
        this.useRenewingSession(cached, metal);
        return;
      }
      this.busy = true;
      const generation = this.generation || 0;
      this.stage.setAttribute('aria-busy', 'true');
      if (!this.viewerReady || this.openedMetal !== metal) this.showMessage('loading');
      this.request = new AbortController();
      const timeout = setTimeout(() => this.request.abort(), 20000);
      try {
        const response = await fetch(endpoint.href, {
          method: 'POST', credentials: 'omit', cache: 'no-store', redirect: 'error',
          headers: {'Content-Type':'application/json'},
          body: JSON.stringify({productHandle:this.config.productHandle, metal}),
          signal: this.request.signal
        });
        if (!response.ok) throw new Error();
        const session = await response.json();
        const url = new URL(session.viewerUrl);
        const expiresAt = Date.parse(session.viewerExpiresAt);
        if (session.mode !== 'VIEW' || session.productHandle !== this.config.productHandle || session.metal !== metal || typeof session.designId !== 'string' || !/^[a-f0-9-]{36}$/i.test(session.designId) || url.protocol !== 'https:' || url.hostname !== 'app.pencildesign.co' || url.port || url.username || url.password || !url.pathname.includes(session.designId) || !url.hash || !Number.isFinite(expiresAt) || expiresAt <= Date.now() + 60000 || expiresAt > Date.now() + 3660000) throw new Error();
        if (!this.isConnected) return;
        this.renewalViews.set(metal, session);
        if (generation !== (this.generation || 0)) { this.queued = true; return; }
        this.renewalFailures = 0;
        this.useRenewingSession(session, metal);
      } catch {
        if (this.isConnected && generation === (this.generation || 0)) {
          if (!this.viewerReady || this.openedMetal !== metal || Date.now() >= this.expiresAt) this.showMessage('error', true);
          this.renewalFailures = (this.renewalFailures || 0) + 1;
          clearTimeout(this.renewal);
          this.renewal = setTimeout(() => {
            if (this.active === 'viewer' && !document.hidden) this.refreshViewer();
          }, Math.min(60000, 5000 * 2 ** Math.min(this.renewalFailures - 1, 4)));
        }
      } finally {
        clearTimeout(timeout);
        this.busy = false;
        this.stage.setAttribute('aria-busy', 'false');
        if (this.queued && this.isConnected) {
          this.queued = false;
          if (this.active === 'viewer') this.refreshViewer();
        }
      }
    }

    useRenewingSession(session, metal) {
      this.dataset.previewUrl = session.viewerUrl;
      this.dataset.previewExpires = session.viewerExpiresAt;
      this.expiresAt = Date.parse(session.viewerExpiresAt);
      this.openedMetal = metal;
      this.selectionDirty = false;
      this.openDesignPreview();
      clearTimeout(this.renewal);
      this.renewal = setTimeout(() => {
        if (this.active === 'viewer' && !document.hidden) this.refreshViewer();
      }, Math.max(1000, this.expiresAt - Date.now() - 300000));
    }

    openMetalPreview() {
      const option = this.selection().selectedOptions.find(item => item.name.toLowerCase() === 'metal');
      const key = option?.value?.trim().toLowerCase().replace(/\s+/g, ' ');
      const preview = this.metalPreviews[key];
      if (!this.variant?.id || !preview || typeof preview.viewerUrl !== 'string' || typeof preview.expiresAt !== 'string') { this.showMessage('unavailable'); return; }
      this.dataset.previewUrl = preview.viewerUrl;
      this.dataset.previewExpires = preview.expiresAt;
      this.selectionDirty = false;
      this.openDesignPreview();
    }

    expirePreview() {
      clearTimeout(this.frameTimeout);
      clearTimeout(this.previewExpiryTimer);
      this.frame.onload = null;
      this.frame.onerror = null;
      this.frame.removeAttribute('src');
      this.viewerReady = false;
      this.showMessage('expired');
      if (this.managedViewer && this.active === 'viewer' && !document.hidden) this.refreshViewer();
    }

    openDesignPreview() {
      let url;
      try {
        url = new URL(this.dataset.previewUrl);
        if (url.protocol !== 'https:' || url.hostname !== 'app.pencildesign.co' || url.port || url.username || url.password) throw new Error();
        const shared = url.pathname === '/share';
        if (shared) {
          if (!/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(url.searchParams.get('token') || '')) throw new Error();
        } else if (!url.hash) throw new Error();
        const expiresAt = Date.parse(this.dataset.previewExpires);
        if (!shared && (!Number.isFinite(expiresAt) || Date.now() >= expiresAt)) { this.expirePreview(); return; }
        clearTimeout(this.previewExpiryTimer);
        if (Number.isFinite(expiresAt)) {
          if (Date.now() >= expiresAt) { this.expirePreview(); return; }
          this.previewExpiryTimer = setTimeout(() => this.expirePreview(), expiresAt - Date.now());
        }
      } catch { this.showMessage('error', true); return; }
      this.selectionDirty = false;
      if (this.viewerReady && this.frame.getAttribute('src') === url.href) {
        this.frame.hidden = false;
        this.message.hidden = true;
        return;
      }
      if (this.frame.getAttribute('src') && this.frame.getAttribute('src') !== url.href) {
        clearTimeout(this.frameTimeout);
        this.frame.onload = null;
        this.frame.onerror = null;
        const previousFrame = this.frame;
        this.frame = previousFrame.cloneNode(false);
        this.frame.removeAttribute('src');
        previousFrame.replaceWith(this.frame);
        this.viewerReady = false;
      }
      this.showMessage('loading');
      this.stage.setAttribute('aria-busy', 'true');
      clearTimeout(this.frameTimeout);
      this.frame.onload = () => {
        if (!this.isConnected) return;
        clearTimeout(this.frameTimeout);
        this.viewerReady = true;
        this.frame.hidden = false;
        this.message.hidden = true;
        this.stage.setAttribute('aria-busy', 'false');
      };
      this.frame.onerror = () => { clearTimeout(this.frameTimeout); this.showMessage('error', true); };
      this.frame.hidden = false;
      this.frame.src = url.href;
      this.frameTimeout = setTimeout(() => { this.frame.onload = null; this.showMessage('error', true); }, 30000);
    }
  });
}






